import React, { useCallback, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/styles";
import {
  colors,
  fontSize,
  fontWeight,
  gaps,
  padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { PolicyDataErrorRecord } from "entities/UIModel/PolicyDataErrorRecord";
import { OptionRenderType } from "controls/global/stewart-checkbox/StewartAutocompleteWithCheckboxes";
import PolicyDataErrorsTable from "./PolicyDataErrorsTable";

const TabContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  gap: gaps.medium1,
});

const SubTabBar = styled(Box)({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  borderBottom: `1px solid ${colors.grey09}`,
  paddingBottom: 0,
});

const subTabButtonStyle = (active: boolean) => ({
  fontWeight: fontWeight.bold1,
  fontSize: fontSize.base,
  background: "none",
  border: "none",
  borderBottom: active ? `2px solid ${colors.blue15}` : "2px solid transparent",
  color: active ? colors.blue15 : colors.grey11,
  padding: "8px 18px 9px",
  cursor: "pointer",
  marginBottom: -1,
  fontFamily: "inherit",
  "&:hover": {
    color: colors.blue15,
  },
});

const ToolbarActions = styled(Box)({
  display: "flex",
  gap: gaps.small1,
  paddingBottom: padding.small1,
});

type SubTab = "new" | "resolved";

interface PolicyDataErrorsTabProps {
  records: PolicyDataErrorRecord[];
}

const PolicyDataErrorsTab = ({ records }: PolicyDataErrorsTabProps) => {
  const [activeSubTab, setActiveSubTab] = useState<SubTab>("new");
  const [filters, setFilters] = useState<Record<string, string | string[] | null>>({});

  // Split records by status
  const newRecords = useMemo(
    () => records.filter((r) => r.policyErrorStatusTypeCode !== "RESOLVED"),
    [records]
  );

  const resolvedRecords = useMemo(
    () => records.filter((r) => r.policyErrorStatusTypeCode === "RESOLVED"),
    [records]
  );

  const activeRecords = activeSubTab === "new" ? newRecords : resolvedRecords;

  // Apply column-level filters
  const filteredRecords = useMemo(() => {
    let result = activeRecords;

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return;

      if (Array.isArray(value)) {
        if (value.length === 0) return;
        result = result.filter((r) => {
          const fieldValue = String(
            r[key as keyof PolicyDataErrorRecord] ?? ""
          );
          return value.includes(fieldValue);
        });
      } else {
        const searchStr = value.toLowerCase().trim();
        if (!searchStr) return;
        result = result.filter((r) => {
          const fieldValue = String(
            r[key as keyof PolicyDataErrorRecord] ?? ""
          ).toLowerCase();
          return fieldValue.includes(searchStr);
        });
      }
    });

    return result;
  }, [activeRecords, filters]);

  const handleFiltersChange = useCallback(
    (name: keyof PolicyDataErrorRecord, value: string | null) => {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleMultiSelectChange = useCallback(
    (
      e: { target: { name: string; value: OptionRenderType[] } },
      value: OptionRenderType[]
    ) => {
      const fieldName = e.target.name;
      const selectedValues = value.map((v) => v.value);
      setFilters((prev) => ({
        ...prev,
        [fieldName]: selectedValues.length > 0 ? selectedValues : null,
      }));
    },
    []
  );

  const handleResetFilters = useCallback(() => {
    setFilters({});
  }, []);

  const handleSubTabChange = useCallback((tab: SubTab) => {
    setActiveSubTab(tab);
    setFilters({});
  }, []);

  const handleExport = useCallback(() => {
    // Export functionality placeholder
  }, []);

  return (
    <TabContainer>
      {/* Sub-tabs: New / Resolved + Toolbar */}
      <SubTabBar>
        <Box sx={{ display: "flex", gap: 0 }}>
          <Box
            component="button"
            sx={subTabButtonStyle(activeSubTab === "new")}
            onClick={() => handleSubTabChange("new")}
          >
            New
          </Box>
          <Box
            component="button"
            sx={subTabButtonStyle(activeSubTab === "resolved")}
            onClick={() => handleSubTabChange("resolved")}
          >
            Resolved
          </Box>
        </Box>
        <ToolbarActions>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleResetFilters}
          >
            Reset
          </ActionButton>
          <ActionButton
            variant="outlined"
            color="secondary"
            onClick={handleExport}
          >
            Export
          </ActionButton>
        </ToolbarActions>
      </SubTabBar>

      {/* Summary */}
      <Box sx={{ display: "flex", alignItems: "center", gap: gaps.small1 }}>
        <Typography
          sx={{
            fontSize: fontSize.small,
            color: colors.grey11,
          }}
        >
          Showing {filteredRecords.length} of {activeRecords.length} record(s)
        </Typography>
      </Box>

      {/* Table */}
      <PolicyDataErrorsTable
        records={filteredRecords}
        onFiltersChange={handleFiltersChange}
        onMultiSelectChange={handleMultiSelectChange}
      />
    </TabContainer>
  );
};

export default PolicyDataErrorsTab;
